import { useEffect, useState } from "react";
import { api, TaxEligibility, TaxReport, S1aEntry, HkdInfo } from "../api";
import { formatVND } from "../utils";
import { exportS1aXlsx } from "../utils/exportS1aXlsx";
import { GlassPanel, KpiCard, Badge, toast, confirm, SkeletonText, ErrorState } from "../components";

const EMPTY_HKD: HkdInfo = { name: "", tax_code: "", address: "" };

const QUARTERS = [1, 2, 3, 4] as const;
const CURRENT_YEAR = new Date().getFullYear();

function getQuarterFrom(year: number, quarter: number): string {
  const map: Record<number, string> = { 1: "01-01", 2: "04-01", 3: "07-01", 4: "10-01" };
  return `${year}-${map[quarter]}`;
}
function getQuarterTo(year: number, quarter: number): string {
  const map: Record<number, string> = { 1: "03-31", 2: "06-30", 3: "09-30", 4: "12-31" };
  return `${year}-${map[quarter]}`;
}

/** Trang Báo cáo thuế HKD (P1-5): Eligibility 3 bậc, Sổ S1a, Khóa kỳ, Generate PDF */
export default function TaxReportPage() {
  const [year, setYear] = useState(CURRENT_YEAR);
  const [quarter, setQuarter] = useState<number>(Math.ceil((new Date().getMonth() + 1) / 3));
  const [eligibility, setEligibility] = useState<TaxEligibility | null>(null);
  const [report, setReport] = useState<TaxReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [locking, setLocking] = useState(false);
  const [hkd, setHkd] = useState<HkdInfo>(EMPTY_HKD);
  const [exportingXlsx, setExportingXlsx] = useState(false);
  const [showHkd, setShowHkd] = useState(false);
  const [savingHkd, setSavingHkd] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generateProgress, setGenerateProgress] = useState<{ done: number; total: number } | null>(null);

  useEffect(() => { api.hkdInfo.get().then(setHkd).catch(() => {}); }, []);

  const exportS1aCsv = async () => {
    if (s1aEntries.length === 0) {
      toast.error("Chưa có dòng S1a để xuất. Bấm Tạo S1a trước.");
      return;
    }
    try {
      const blob = await api.s1a.download(year, quarter);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `S1a-HKD_Q${quarter}_${year}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Đã tải file CSV S1a.");
    } catch (e: unknown) {
      toast.error((e as Error).message || "Lỗi xuất CSV S1A");
    }
  };

  const handleExportS1aXlsx = async () => {
    if (s1aEntries.length === 0) {
      toast.error("Chưa có dòng S1a để xuất. Bấm Tạo S1a trước.");
      return;
    }
    setExportingXlsx(true);
    try {
      await exportS1aXlsx({ hkd, entries: s1aEntries, year, quarter });
      toast.success("Đã tải file Excel S1a-HKD.");
    } catch (e: unknown) {
      toast.error((e as Error).message || "Lỗi xuất Excel S1A");
    } finally {
      setExportingXlsx(false);
    }
  };

  const saveHkd = async () => {
    setSavingHkd(true);
    try {
      await api.hkdInfo.update(hkd);
      toast.success("Đã lưu thông tin hộ kinh doanh");
      setShowHkd(false);
    } catch (e: unknown) {
      toast.error((e as Error).message || "Lỗi lưu");
    } finally {
      setSavingHkd(false);
    }
  };

  const reload = () => {
    setLoading(true);
    setError(null);
    Promise.all([
      api.tax.eligibility(year).catch(() => null),
      api.tax.report(year, quarter).catch(() => null),
    ])
      .then(([elig, rep]) => {
        setEligibility(elig);
        setReport(rep);
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    Promise.all([
      api.tax.eligibility(year).catch(() => null),
      api.tax.report(year, quarter).catch(() => null),
    ])
      .then(([elig, rep]) => {
        if (!alive) return;
        setEligibility(elig);
        setReport(rep);
      })
      .catch((e: Error) => alive && setError(e.message))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [year, quarter]);

  const statusLabel = (status: string): { text: string; variant: "ok" | "warn" | "bad" | "info" } => {
    const map: Record<string, { text: string; variant: "ok" | "warn" | "bad" | "info" }> = {
      exempt: { text: "Miễn thuế", variant: "ok" },
      fixed: { text: "Thuế khoán", variant: "warn" },
      declared: { text: "Kê khai", variant: "bad" },
    };
    return map[status] || { text: status, variant: "info" };
  };

  const handleLock = async () => {
    if (!await confirm({ title: "Khoá kỳ thuế", message: `Khoá kỳ Q${quarter}/${year}? Không thể sửa đổi sau khi khoá.`, danger: true, confirmLabel: "Khoá" })) return;
    setLocking(true);
    try {
      await api.tax.lockPeriod({ year, quarter });
      // Reload report
      const rep = await api.tax.report(year, quarter);
      setReport(rep);
    } catch (e: unknown) {
      toast.error((e as Error).message || "Lỗi khoá kỳ");
    } finally {
      setLocking(false);
    }
  };

  const handlePrint = () => {
    if (s1aEntries.length === 0) {
      toast.error("Chưa có dòng S1a để in. Bấm Tạo S1a trước.");
      return;
    }
    document.body.classList.add("print-s1a");
    window.print();
    setTimeout(() => document.body.classList.remove("print-s1a"), 500);
  };

  const handleGenerateS1a = async () => {
    const ok = await confirm({
      title: `Tạo S1a Q${quarter}/${year}`,
      message: `Sẽ xoá entries cũ (nếu có) trong quý này và tạo lại. Mất khoảng 30s–2 phút tuỳ dữ liệu.`,
      danger: false,
      confirmLabel: "Tạo S1a",
    });
    if (!ok) return;
    setGenerating(true);
    setGenerateProgress(null);
    try {
      const from = getQuarterFrom(year, quarter);
      const to = getQuarterTo(year, quarter);
      const start = new Date(`${from}T00:00:00`);
      const end = new Date(`${to}T00:00:00`);
      const totalDays = Math.floor((end.getTime() - start.getTime()) / 86400000) + 1;
      let totalInserted = 0;
      let totalRevenue = 0;
      const errors: string[] = [];

      for (let i = 0; i < totalDays; i++) {
        const d = new Date(start.getTime() + i * 86400000);
        const date = [
          d.getFullYear(),
          String(d.getMonth() + 1).padStart(2, "0"),
          String(d.getDate()).padStart(2, "0"),
        ].join("-");
        setGenerateProgress({ done: i, total: totalDays });
        try {
          const result = await api.s1a.generateDate(date);
          if (!result.skipped) {
            totalInserted += (result.vat_items || 0) + (result.bangke_items || 0);
            totalRevenue += result.total_revenue || 0;
          }
        } catch (err) {
          errors.push(`${date}: ${(err as Error).message}`);
        }
        setGenerateProgress({ done: i + 1, total: totalDays });
      }

      const revenueText = totalRevenue.toLocaleString("vi-VN");
      if (errors.length === totalDays) {
        const first = (errors[0] || "").split(": ").pop() || "";
        toast.error(`Không tạo được (tất cả ${totalDays} ngày lỗi). ${first ? `Lý do: ${first}` : "Kiểm tra đăng nhập / quyền."}`);
      } else if (errors.length > 0) {
        toast.error(`Tạo xong nhưng có ${errors.length} ngày lỗi. Ngày đầu: ${errors[0]}`);
      } else {
        toast.success(`Đã tạo ${totalInserted} dòng S1a, tổng ${revenueText}đ`);
      }
      // Reload report
      const rep = await api.tax.report(year, quarter);
      setReport(rep);
    } catch (e: unknown) {
      // Friendly message cho lỗi admin thay vì raw "admin only"
      const err = e as Error;
      const raw = err?.message || "";
      const friendly = raw === "admin only"
        ? "Cần quyền admin. Vào Admin Tools (menu trái) để xác thực, rồi thử lại."
        : raw || "Lỗi tạo S1a";
      toast.error(friendly);
    } finally {
      setGenerating(false);
      setGenerateProgress(null);
    }
  };

  const isLocked = report?.filing && (report.filing as Record<string, unknown>).is_locked === 1;
  const s1aEntries: S1aEntry[] = report?.s1a_entries || [];

  // Totals for S1a
  const s1aTotalRevenue = s1aEntries.reduce((s, e) => s + (e.revenue || 0), 0);
  const s1aTotalCost = s1aEntries.reduce((s, e) => s + (e.cost || 0), 0);
  const s1aYearRevenue = report?.s1a_revenue_year ?? s1aTotalRevenue;
  const s1aQuarterRevenue = report?.s1a_revenue_quarter ?? s1aTotalRevenue;

  if (loading) return <SkeletonText lines={8} />;
  if (error) return <ErrorState error={error} onRetry={reload} />;

  return (
    <>
      {/* Bộ lọc năm + quý */}
      <div className="daterange" style={{ marginBottom: 16 }}>
        <div className="field">
          <label>Năm</label>
          <select value={year} onChange={(e) => setYear(Number(e.target.value))}>
            {[CURRENT_YEAR, CURRENT_YEAR - 1, CURRENT_YEAR - 2].map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>Quý</label>
          <div className="tabs">
            {QUARTERS.map((q) => (
              <button
                key={q}
                className={`tab-item ${quarter === q ? "active" : ""}`}
                onClick={() => setQuarter(q)}
              >
                Q{q}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* KPI cards */}
      <div className="kpi-grid">
        <KpiCard
          label="Doanh thu S1a năm"
          value={formatVND(s1aYearRevenue)}
          note={`SP đã đánh dấu S1a · Năm ${year}`}
          dotColor="blue"
        />
        <KpiCard
          label="Doanh thu S1a quý"
          value={formatVND(s1aQuarterRevenue)}
          note={`SP đã đánh dấu S1a · Q${quarter}/${year}`}
          dotColor="blue"
        />
        <KpiCard
          label="Thuế phải nộp"
          value={formatVND(report?.tax_amount || 0)}
          note={`Thuế suất: ${((report?.tax_rate || 0) * 100).toFixed(1)}%`}
          dotColor={report?.tax_amount ? "warn" : "ok"}
        />
        <KpiCard
          label="Trạng thái"
          value={statusLabel(report?.tax_status || eligibility?.tax_status || "exempt").text}
          dotColor={
            statusLabel(report?.tax_status || eligibility?.tax_status || "exempt").variant === "ok" ? "ok" :
            statusLabel(report?.tax_status || eligibility?.tax_status || "exempt").variant === "warn" ? "warn" : "bad"
          }
          note={isLocked ? "Đã khóa kỳ" : "Đang mở"}
        />
      </div>

      {/* Section 1: Eligibility — Nghị định 141/2026/NĐ-CP */}
      <GlassPanel title="Bậc thuế HKD (NĐ 141/2026)" badge={`${year}`} badgeVariant="info">
        <div className="list">
          <div className="list-item">
            <div>
              <div className="name">Bậc 1: Miễn thuế</div>
              <div className="meta">Doanh thu ≤ 1 tỷ/năm — không chịu GTGT, TNCN</div>
            </div>
            <div className="right">
              <Badge variant={eligibility?.tax_status === "exempt" ? "ok" : ""}>
                {eligibility?.tax_status === "exempt" ? "Đang áp dụng" : "—"}
              </Badge>
            </div>
          </div>
          <div className="list-item">
            <div>
              <div className="name">Bậc 2: Kê khai (1.5% DV / 2% TM-TT)</div>
              <div className="meta">Doanh thu &gt; 1 tỷ/năm — bắt buộc HĐĐT khởi tạo từ MTT có kết nối CQT</div>
            </div>
            <div className="right">
              <Badge variant={eligibility?.tax_status === "declared" ? "bad" : ""}>
                {eligibility?.tax_status === "declared" ? "Đang áp dụng" : "—"}
              </Badge>
            </div>
          </div>
        </div>
        {eligibility && (
          <div style={{ marginTop: 12, padding: "8px 12px", background: "rgba(52,211,153,0.06)", borderRadius: 8, fontSize: 13 }}>
            Doanh thu năm {year}: <strong>{formatVND(eligibility.revenue_total)}</strong>
            {" · "}Ngưỡng miễn: {formatVND(eligibility.thresholds.exempt)}
            {eligibility.revenue_total > 1_000_000_000 && (
              <div style={{ marginTop: 6, color: "#fbbf24" }}>
                ⚠ Doanh thu vượt 1 tỷ — bắt buộc dùng hóa đơn điện tử khởi tạo từ máy tính tiền (kết nối DL với CQT)
              </div>
            )}
          </div>
        )}
      </GlassPanel>

      {/* Section 2: Sổ S1a — virtual_orders trong quý */}
      <GlassPanel title={`Sổ S1a — Q${quarter}/${year}`} badge={`${s1aEntries.length} dòng`} badgeVariant="info">
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
          <button
            className="btn"
            onClick={handleGenerateS1a}
            disabled={generating}
            style={{ background: "var(--accent-blue)", color: "white", fontSize: 12 }}
            title="Tạo/cập nhật lại sổ doanh thu S1A cho quý này từ dữ liệu bán hàng (ghi đè các ngày chưa khóa)"
          >
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>auto_awesome</span>
            {generating && generateProgress
              ? `Đang tạo ${generateProgress.done}/${generateProgress.total}`
              : generating ? "Đang tạo..." : "Tạo S1a"}
          </button>
          <button className="btn btn-ghost" onClick={() => setShowHkd(true)} style={{ fontSize: 12 }}>
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>store</span>
            Thông tin HKD
          </button>
          <button className="btn btn-secondary" onClick={handleExportS1aXlsx} disabled={exportingXlsx}>
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>download</span>
            {exportingXlsx ? "Đang xuất…" : "Xuất S1A (Excel)"}
          </button>
          <button className="btn btn-ghost" onClick={exportS1aCsv} style={{ fontSize: 12 }}>
            Xuất CSV
          </button>
          <button className="btn btn-secondary" onClick={handlePrint}>
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>print</span>
            In sổ S1a
          </button>
        </div>
        {showHkd && (
          <div style={{ background: "var(--surface-2,#24242f)", border: "1px solid var(--border,#2a2a38)", borderRadius: 10, padding: 12, marginBottom: 10, display: "flex", flexDirection: "column", gap: 8 }}>
            <div style={{ fontWeight: 600, fontSize: 13 }}>Thông tin hộ kinh doanh (in trên sổ S1A)</div>
            <div className="field">
              <label>Tên hộ kinh doanh</label>
              <input value={hkd.name} onChange={(e) => setHkd({ ...hkd, name: e.target.value })} />
            </div>
            <div className="field">
              <label>Mã số thuế</label>
              <input value={hkd.tax_code} onChange={(e) => setHkd({ ...hkd, tax_code: e.target.value })} />
            </div>
            <div className="field">
              <label>Địa chỉ</label>
              <input value={hkd.address} onChange={(e) => setHkd({ ...hkd, address: e.target.value })} />
            </div>
            <div className="field">
              <label>Chủ hộ kinh doanh / người đại diện</label>
              <input value={hkd.owner || ""} onChange={(e) => setHkd({ ...hkd, owner: e.target.value })} />
            </div>
            <div className="field">
              <label>Người lập sổ</label>
              <input value={hkd.preparer || ""} onChange={(e) => setHkd({ ...hkd, preparer: e.target.value })} />
            </div>
            <div className="field">
              <label>Địa điểm kinh doanh</label>
              <input value={hkd.business_location || ""} onChange={(e) => setHkd({ ...hkd, business_location: e.target.value })} />
            </div>
            <div className="field">
              <label>Ngành nghề kinh doanh</label>
              <input value={hkd.business_line || ""} onChange={(e) => setHkd({ ...hkd, business_line: e.target.value })} />
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button className="btn btn-ghost" onClick={() => setShowHkd(false)}>Đóng</button>
              <button className="btn" onClick={saveHkd} disabled={savingHkd}>{savingHkd ? "Đang lưu…" : "Lưu"}</button>
            </div>
          </div>
        )}
        {s1aEntries.length === 0 ? (
          <div className="empty">Chưa có dữ liệu S1a cho quý này. Chạy cron generate-s1a để tạo.</div>
        ) : (
          <>
            <div className="table-wrap s1a-print-area" data-print-title={`Sổ S1a — Q${quarter}/${year}`}>
              <table>
                <thead>
                  <tr>
                    <th>Ngày</th>
                    <th>Mô tả</th>
                    <th style={{ textAlign: "center" }}>Loại</th>
                    <th style={{ textAlign: "right" }}>Doanh thu</th>
                    <th style={{ textAlign: "right" }}>Giá vốn</th>
                  </tr>
                </thead>
                <tbody>
                  {s1aEntries.map((e) => (
                    <tr key={e.id}>
                      <td>{e.entry_date}</td>
                      <td>{e.description}</td>
                      <td style={{ textAlign: "center" }}>
                        <Badge variant={e.sub_type === "vat" ? "ok" : "info"}>
                          {e.sub_type === "vat" ? "VAT" : "Bảng kê"}
                        </Badge>
                      </td>
                      <td style={{ textAlign: "right" }}>{formatVND(e.revenue)}</td>
                      <td style={{ textAlign: "right" }}>{formatVND(e.cost)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ fontWeight: 700 }}>
                    <td colSpan={3}>Tổng cộng</td>
                    <td style={{ textAlign: "right" }}>{formatVND(s1aTotalRevenue)}</td>
                    <td style={{ textAlign: "right" }}>{formatVND(s1aTotalCost)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </>
        )}
      </GlassPanel>

      {/* Section 3: Khóa kỳ */}
      <GlassPanel title="Thao tác" badge={isLocked ? "Đã khóa" : "Mở"} badgeVariant={isLocked ? "bad" : "ok"}>
        {isLocked ? (
          <div style={{ padding: "12px 0", color: "var(--accent-red)", fontWeight: 600 }}>
            Kỳ Q{quarter}/{year} đã được khóa. Không thể chỉnh sửa dữ liệu trong kỳ này.
          </div>
        ) : (
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", padding: "8px 0" }}>
            <button className="btn" onClick={handleLock} disabled={locking} title="Khóa sổ S1A của kỳ này — chốt số liệu, không cho tạo lại trừ khi ghi đè có chủ ý">
              <span className="material-symbols-outlined" style={{ fontSize: 16 }}>lock</span>
              {locking ? "Đang khóa…" : "Khóa kỳ này"}
            </button>
            <button className="btn btn-secondary" onClick={handlePrint}>
              <span className="material-symbols-outlined" style={{ fontSize: 16 }}>print</span>
              In sổ S1a
            </button>
          </div>
        )}
      </GlassPanel>
    </>
  );
}
